import { ArrowDropDown, ArrowDropUp, Close, FilterList } from '@mui/icons-material';
import { Button, Divider, Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, useState } from 'react';
import { NeighborhoodLotsDropdown } from '../../components';
import { NeighborhoodAutocompleteDropdown } from '../../components/dropdowns/neighborhood-autocomplete';

interface IBuildSelectedOptionsFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  selectedLotId: string | number;
  setSelectedNeighborhoodId: (val: any) => void;
  setSelectedLotId: (val: any) => void;
  selectedNeighborhoodId: string | number;
}

export const BuildSelectedOptionsFilters: FC<IBuildSelectedOptionsFilters> = ({
  isLoading,
  applyFilters,
  selectedNeighborhoodId,
  selectedLotId,
  setSelectedNeighborhoodId,
  setSelectedLotId
}) => {
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={8} md={4} lg={3}>
          <NeighborhoodAutocompleteDropdown
            disableClearable={false}
            value={selectedNeighborhoodId}
            handleChange={value => setSelectedNeighborhoodId(value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3} lg={2}>
          <NeighborhoodLotsDropdown
            value={selectedLotId}
            isDisabled={!selectedNeighborhoodId}
            handleChange={value => setSelectedLotId(value)}
            apiParams={{ neighborhoodId: selectedNeighborhoodId, isSpec: false }}
          />
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              color="inherit"
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSelectedNeighborhoodId('');
                setSelectedLotId('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
